/**
 * «i» товч — дэлгэрэнгүйг ХУУДСЫГ дүүргэхгүйгээр хэлнэ.
 *
 * Дашбоард дээр «өмнөх сараас 18% өссөн» гэх мэт тайлбар олон байна.
 * Бүгдийг мөрөнд бичвэл хуудас урт болж, гол тоо алга болно — тиймээс
 * тоо нь ил, учир шалтгаан нь энд.
 *
 * ⚠️ ЦОНХ НЬ `body`-Д ГАРНА (portal). Эцэг элемент нь `overflow-hidden`
 *    байвал (KPI карт яг тийм) энгийн `absolute` цонх ТАСАРЧ харагдана
 *    — 2026-09-25-нд эзэмшигч тэр алдааг зурагтайгаа мэдэгдсэн.
 *    Portal нь DOM-ийн мод дотор биш, дэлгэц дээр байрлана.
 * ⚠️ ЗӨВХӨН ХУЛГАНА БИШ. Гар (Tab → Enter), хүрэлтээр нээгдэнэ:
 *    `title` шинжид найдвал гар, дэлгэцийн уншигчид хүрэхгүй.
 * ⚠️ Агуулга нь ЗӨВХӨН нэмэлт тайлбар байх ёстой. Энд Л байгаа тоо
 *    байж БОЛОХГҮЙ — хулганагүй хүн түүнийг олохгүй.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

/** Цонхны өргөн, px — байрлалыг тооцоход хэрэгтэй. */
const WIDTH = 264;
const GAP = 8;

export function InfoTip({ label, children }: {
  /** Дэлгэцийн уншигчид хэлэх нэр — «Энэ сарын борлуулалт, дэлгэрэнгүй» */
  label: string;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);

  /** Товчны байрлалаас цонхны байрлалыг бодно — дэлгэцээс гарахгүй. */
  const place = () => {
    const r = btnRef.current?.getBoundingClientRect();
    if (!r) return;
    const left = Math.min(
      Math.max(GAP, r.right - WIDTH),
      Math.max(GAP, window.innerWidth - WIDTH - GAP),
    );
    setPos({ top: r.bottom + GAP, left });
  };

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    // Хуудас гүйхэд цонх товчноосоо салах ёсгүй
    const onMove = () => place();
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    return () => {
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  }, [open]);

  const show = () => {
    place();
    setOpen(true);
  };

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        aria-label={label}
        aria-expanded={open}
        onClick={() => (open ? setOpen(false) : show())}
        onMouseEnter={show}
        onMouseLeave={() => setOpen(false)}
        onFocus={show}
        onBlur={() => setOpen(false)}
        className={`grid size-[18px] shrink-0 place-items-center rounded-full border
          text-[11px] leading-none transition ${
            open ? 'border-accent-700 bg-accent-100 text-accent-700' : 'border-hair text-ink-3'
          }`}
      >
        i
      </button>

      {open && pos
        ? createPortal(
            <div
              role="tooltip"
              style={{ top: pos.top, left: pos.left, width: WIDTH }}
              className="fixed z-[60] rounded-card border border-hair bg-panel px-3.5 py-3
                text-left text-[12.5px] leading-relaxed text-ink-2 shadow-pop"
            >
              {children}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}

/**
 * «Энэ юу вэ? / Заавар» — «i» товчны СТАНДАРТ агуулга (захиалагчийн
 * цэгцлэл №1, 2026-10-05). Ажилтан тоог системээс гаралгүй ойлгоно.
 *
 * `about` — тоо юуг хэлж байна, юу орж/ордоггүй.
 * `howTo` — үүнийг хараад ЮУ ХИЙХ вэ, хаанаас. Зөвхөн мэдээллийн
 *           картад заавал биш.
 */
export function Help({ about, howTo }: { about: ReactNode; howTo?: ReactNode }) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-col gap-0.5">
        <span className="text-micro font-semibold text-ink">Энэ юу вэ?</span>
        <span>{about}</span>
      </div>
      {howTo ? (
        <div className="flex flex-col gap-0.5">
          <span className="text-micro font-semibold text-ink">Заавар</span>
          <span>{howTo}</span>
        </div>
      ) : null}
    </div>
  );
}
